// Connector + mock-connector client.
//
// Lifted out of `../api` so the file boundary matches the backend
// `src/railyard/routes/connectors.py` + `routes/mock_connectors.py`.
// Adding a new connector touches this file, the routes module, and the
// per-tenant vault wiring — see "Adding MCP connectors" in CLAUDE.md.

import type { components } from "@/lib/api-types";
import {
  connectorDisconnectSlugs,
  connectorOAuthStartPath,
  connectorVerifyPath,
  type BackendRoutedConnector,
  type ConnectorBackend,
} from "@/lib/connector-backend";

import {
  API,
  apiFetch,
  requestJson,
  requestOk,
} from "../api";


type Schemas = components["schemas"];


// ---------------------------------------------------------------------------
// Connectors
// ---------------------------------------------------------------------------

// Generated from routes/connectors.py:Connector. The hand-rolled type
// had `description` and `icon` as required strings, but dynamic-
// registration connectors omit them when the underlying MCP spec
// doesn't declare them — now correctly typed as optional.
export type Connector = Schemas["Connector"];

export async function listConnectors(): Promise<Connector[]> {
  const r = await apiFetch(`${API}/api/connectors`);
  if (!r.ok) return [];
  const data = await r.json();
  return Array.isArray(data) ? data : [];
}

/** Fetch an OAuth authorize URL (with auth) and navigate the browser to it. */
async function beginOAuth(path: string): Promise<void> {
  const returnTo = encodeURIComponent(window.location.origin);
  const { authorize_url } = await requestJson<{ authorize_url?: string }>(
    `${API}${path}?return_to=${returnTo}`,
  );
  if (!authorize_url) throw new Error("OAuth start returned no authorize_url");
  window.location.href = authorize_url;
}

/** Shared 404-tolerant disconnect for any connector. The vault may already
 * have been cleared (e.g. retried disconnect, server-side cleanup); treat
 * 404 as success so the UI doesn't surface noise on an idempotent action. */
async function disconnectConnector(slug: string): Promise<void> {
  await requestOk(
    `${API}/api/connectors/${slug}`,
    { method: "DELETE" },
    { allowStatus: [404] },
  );
}

export async function startSelectedConnectorOAuth(
  connector: BackendRoutedConnector,
): Promise<void> {
  await beginOAuth(connectorOAuthStartPath(connector));
}

export async function disconnectSelectedConnector(
  connector: BackendRoutedConnector,
): Promise<void> {
  const results = await Promise.allSettled(
    connectorDisconnectSlugs(connector).map((slug) => disconnectConnector(slug)),
  );
  const failed = results.find(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (failed) throw failed.reason;
}

export async function startSlackOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/slack/oauth/start");
}

export async function disconnectSlack(): Promise<void> {
  await disconnectConnector("slack");
}

export async function startGrainOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/grain/oauth/start");
}

export async function disconnectGrain(): Promise<void> {
  await disconnectConnector("grain");
}

export async function startKenshoOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/kensho/oauth/start");
}

export async function disconnectKensho(): Promise<void> {
  await disconnectConnector("kensho");
}

export async function startCbinsightsOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/cbinsights/oauth/start");
}

export async function disconnectCbinsights(): Promise<void> {
  await disconnectConnector("cbinsights");
}

export async function startDatasiteOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/datasite/oauth/start");
}

export async function disconnectDatasite(): Promise<void> {
  await disconnectConnector("datasite");
}

export async function startIbisWorldOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/ibisworld/oauth/start");
}

export async function disconnectIbisWorld(): Promise<void> {
  await disconnectConnector("ibisworld");
}

export async function startFactsetOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/factset/oauth/start");
}

export async function disconnectFactset(): Promise<void> {
  await disconnectConnector("factset");
}

export async function startPitchbookOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/pitchbook/oauth/start");
}

export async function disconnectPitchbook(): Promise<void> {
  await disconnectConnector("pitchbook");
}

export async function startAffinityOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/affinity/oauth/start");
}

export async function disconnectAffinity(): Promise<void> {
  await disconnectConnector("affinity");
}

export async function startApolloioOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/apolloio/oauth/start");
}

export async function disconnectApolloio(): Promise<void> {
  await disconnectConnector("apolloio");
}

export async function startOtterOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/otter/oauth/start");
}

export async function disconnectOtter(): Promise<void> {
  await disconnectConnector("otter");
}

export async function startApifyOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/apify/oauth/start");
}

export async function disconnectApify(): Promise<void> {
  await disconnectConnector("apify");
}

export async function startMetalOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/metal/oauth/start");
}

export async function disconnectMetal(): Promise<void> {
  await disconnectConnector("metal");
}

export async function startMoodysOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/moodys/oauth/start");
}

export async function disconnectMoodys(): Promise<void> {
  await disconnectConnector("moodys");
}

export async function startCocounselOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/cocounsel/oauth/start");
}

export async function disconnectCocounsel(): Promise<void> {
  await disconnectConnector("cocounsel");
}


// ---------------------------------------------------------------------------
// Productivity / pipeline connectors (Zapier, HubSpot, Gmail, Google Drive).
// Same OAuth shape — listed
// below the Mock section in the original file, hence the order here.
// ---------------------------------------------------------------------------

export async function startZapierOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/zapier/oauth/start");
}

export async function disconnectZapier(): Promise<void> {
  await disconnectConnector("zapier");
}

export async function startHubspotOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/hubspot/oauth/start");
}

export async function disconnectHubspot(): Promise<void> {
  await disconnectConnector("hubspot");
}

export async function startGmailOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/gmail/oauth/start");
}

export async function disconnectGmail(): Promise<void> {
  await disconnectConnector("gmail");
}

export async function startGoogleDriveOAuth(): Promise<void> {
  await beginOAuth("/api/connectors/google-drive/oauth/start");
}

export async function disconnectGoogleDrive(): Promise<void> {
  await disconnectConnector("google-drive");
}

/** Record a proxied-backend connection after its OAuth return. Resolves
 * false on a non-OK verify instead of throwing. */
export async function verifyProxiedConnection(
  integration: string,
  backend: ConnectorBackend,
): Promise<boolean> {
  const r = await apiFetch(`${API}${connectorVerifyPath(integration, backend)}`, {
    method: "POST",
  });
  return r.ok;
}
